import * as React from "react";
import { ImageLoadMeasurement, startImageLoad } from "../lib/analytics";

const PlaceholderSvg = () => (
  <svg
    className="w-12 h-12 text-theme-color opacity-40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
  >
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

const ErrorSvg = () => (
  <svg
    className="w-10 h-10 text-theme-color opacity-30"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
  >
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);

/**
 * An artifact image, preferring our mirrored copy.
 *
 * `src` is whichever source we would rather serve — our own bucket, when the
 * backend found a mirror for this image. `fallbackSrc` is the museum's own
 * URL, used only if the first one fails to load.
 *
 * The fallback is what makes mirroring safe to roll out incrementally: a blob
 * that has not been copied yet, a bucket that is misconfigured, a key that was
 * purged — each degrades to exactly today's behaviour rather than to a broken
 * image. Nothing has to be mirrored for the site to keep working.
 *
 * `museum` only labels the image in analytics, so a museum whose images fail
 * shows up as that museum rather than as a site-wide dip.
 */
export const ImageWithPlaceholder: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  museum?: string;
}> = ({ src, alt, className = "", fallbackSrc, museum }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  // Which URL we are currently attempting. Starts at `src`; one failure moves
  // it to `fallbackSrc`, and a failure there gives up.
  const [current, setCurrent] = React.useState(src);

  // A card can be recycled onto a different artifact as results change, so
  // reset rather than leaving the previous artifact's image or error state.
  React.useEffect(() => {
    setCurrent(src);
    setLoaded(false);
    setFailed(false);
  }, [src, fallbackSrc]);

  // One measurement per image shown. A layout effect rather than a plain one
  // so it exists before the browser can report the outcome, which for a
  // cached image can be almost immediate.
  const measurement = React.useRef<ImageLoadMeasurement | null>(null);
  React.useLayoutEffect(() => {
    measurement.current = startImageLoad(museum);
  }, [src, fallbackSrc]);

  const handleLoad = React.useCallback(() => {
    measurement.current?.loaded(current !== src);
    setLoaded(true);
  }, [current, src]);

  const handleError = React.useCallback(() => {
    if (fallbackSrc && current !== fallbackSrc) {
      setCurrent(fallbackSrc);
      return;
    }
    measurement.current?.failed();
    setFailed(true);
  }, [current, fallbackSrc]);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-theme-color border border-dashed border-theme-color min-w-[150px] min-h-[150px] ${className}`}
      >
        <ErrorSvg />
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className={`relative min-w-[200px] min-h-[200px] ${className}`}>
        <div className="absolute inset-0 flex items-center justify-center bg-theme-color border border-theme-color animate-pulse">
          <PlaceholderSvg />
        </div>
        <img
          src={current}
          alt={alt}
          className="opacity-0 w-full h-full"
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }

  return (
    <img
      src={current}
      alt={alt}
      className={`${className} transition-opacity duration-300`}
      // Kept on the loaded element too: a mirrored URL can start returning 404
      // after it has rendered once (a purge mid-session), and without this the
      // fallback would never run for an image the browser had already shown.
      onError={handleError}
    />
  );
};
